import React from 'react';

import {AnalyticsVisualizationColorPalette as ColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import {areLabeledDatasets, Datasets, LabelCompatibleType, LegendConfig} from 'ui-library/charting/Types';
import ImmutableSet from 'util/ImmutableSet';

import DiscreteSeriesLegend, {
  DiscreteSeriesLegendProps,
  estimateDiscreteSeriesLegendHeight,
} from './DiscreteSeriesLegend';
import GradientSeriesLegend, {
  estimateGradientSeriesLegendWidth,
  GradientSeriesLegendProps,
} from './GradientSeriesLegend';
import {LegendLocation} from './LegendConstants';
import {SeriesColorConfig} from './utils/getSeriesColorConfig';

interface BaseSeriesLegendOutput {
  remainingChartHeight: number;
  remainingChartWidth: number;
  showLegend: boolean;
}
export type DiscreteSeriesLegendOutput = BaseSeriesLegendOutput & {
  legendLocation: LegendLocation.TOP | LegendLocation.RIGHT | LegendLocation.LEFT;
  legendProps: DiscreteSeriesLegendProps;
};
type GradientSeriesLegendOutput = BaseSeriesLegendOutput & {
  legendLocation: LegendLocation.RIGHT;
  legendProps: GradientSeriesLegendProps;
};
export type UseSeriesLegendOutput = DiscreteSeriesLegendOutput | GradientSeriesLegendOutput;

export function useSeriesLegend(
  colorConfig: SeriesColorConfig,
  chartHeight: number,
  chartWidth: number,
  datasets: Datasets,
  excludedSeries?: ImmutableSet<LabelCompatibleType>,
  legend?: LegendConfig,
  onClickLegendItem?: (label: LabelCompatibleType) => void
): UseSeriesLegendOutput {
  if (!legend || !areLabeledDatasets(datasets)) {
    return {
      legendLocation: LegendLocation.TOP,
      legendProps: {colorConfig} as DiscreteSeriesLegendProps,
      remainingChartHeight: chartHeight,
      remainingChartWidth: chartWidth,
      showLegend: false,
    };
  } else if (colorConfig.colorPalette === ColorPalette.DISCRETE) {
    const estimatedLegendHeight = estimateDiscreteSeriesLegendHeight(colorConfig.colorScale, chartWidth);
    return {
      legendLocation: LegendLocation.TOP,
      legendProps: {colorConfig, excludedSeries, onClickLegendItem},
      remainingChartHeight: chartHeight - estimatedLegendHeight,
      remainingChartWidth: chartWidth,
      showLegend: true,
    };
  } else {
    const legendDomain = colorConfig.colorScale.domain();
    const estimatedLegendWidth = estimateGradientSeriesLegendWidth(
      legend.label,
      legendDomain[0],
      legendDomain[1]
    );
    const remainingChartWidth = chartWidth - estimatedLegendWidth;
    return {
      legendLocation: LegendLocation.RIGHT,
      legendProps: {
        colorConfig,
        chartHeight,
        chartWidth: remainingChartWidth,
        label: legend.label,
      },
      remainingChartHeight: chartHeight,
      remainingChartWidth,
      showLegend: true,
    };
  }
}

export type SeriesLegendProps = DiscreteSeriesLegendProps | GradientSeriesLegendProps;

export default function SeriesLegend(props: SeriesLegendProps): JSX.Element {
  return props.colorConfig.colorPalette === ColorPalette.DISCRETE ? (
    <DiscreteSeriesLegend {...(props as DiscreteSeriesLegendProps)} />
  ) : (
    <GradientSeriesLegend {...(props as GradientSeriesLegendProps)} />
  );
}
